/**
 * IDB Key Manager
 *
 * Single source of truth for all IndexedDB database naming.
 * Every service that creates an IndexedDB database MUST use this module
 * to generate and parse keys, ensuring consistent naming and cleanup.
 */

export type IDBKeyType =
  | 'entity-sync'     // localFirstSyncService: Y.Doc persistence per entity
  | 'room-crdt'       // crdtServiceWS: Y.Doc persistence per room (doc:, note:, terminal:)
  | 'space-layout'    // crdtServiceWS: space layout persistence
  | 'space-entities'  // spaceEntityIDB: per-space JSON snapshot of entities
  | 'global-entities' // globalEntityIDB: per-dataspace JSON snapshot of global entities (Space, Integration*, MCPConnection)
  | 'entity-by-id'    // entityByIdIDB: shared LRU of fully-loaded entity skeletons
  | 'file-editor'     // FileEditorApplication: file content persistence
  | 'manifest';       // idbManifest: self-tracking manifest

interface ParsedIDBKey {
  type: IDBKeyType;
  id: string;
  entityType?: string;
  workspaceId?: string;
}

const PREFIX = 'river';
const SEPARATOR = '__';

/**
 * Build a deterministic IDB database name from structured parameters.
 *
 * Format: river__{type}__{id}  (with optional __{entityType}__{workspaceId})
 *
 * Using double-underscore separator avoids collisions with entity IDs
 * and room names that contain single underscores or colons.
 */
export function buildIDBKey(
  type: IDBKeyType,
  id: string,
  meta?: { entityType?: string; workspaceId?: string }
): string {
  switch (type) {
    case 'entity-sync':
      return `${PREFIX}${SEPARATOR}entity-sync${SEPARATOR}${meta?.workspaceId ?? 'default'}${SEPARATOR}${meta?.entityType ?? 'unknown'}${SEPARATOR}${id}`;
    case 'room-crdt':
      return `${PREFIX}${SEPARATOR}room-crdt${SEPARATOR}${id}`;
    case 'space-layout':
      return `${PREFIX}${SEPARATOR}space-layout${SEPARATOR}${id}`;
    case 'space-entities':
      return `${PREFIX}${SEPARATOR}space-entities${SEPARATOR}${id}`;
    case 'global-entities':
      return `${PREFIX}${SEPARATOR}global-entities${SEPARATOR}${id}`;
    case 'entity-by-id':
      return `${PREFIX}${SEPARATOR}entity-by-id`;
    case 'file-editor':
      return `${PREFIX}${SEPARATOR}file-editor${SEPARATOR}${id}`;
    case 'manifest':
      return `${PREFIX}${SEPARATOR}manifest`;
  }
}

/**
 * Parse a structured IDB key back into its components.
 * Returns null for unrecognised keys (e.g. legacy names).
 */
export function parseIDBKey(key: string): ParsedIDBKey | null {
  if (!key.startsWith(PREFIX + SEPARATOR)) return null;

  const parts = key.split(SEPARATOR);
  // parts[0] is always 'river'
  const type = parts[1] as IDBKeyType;

  switch (type) {
    case 'entity-sync': {
      // river__entity-sync__workspaceId__entityType__entityId
      if (parts.length < 5) return null;
      return {
        type,
        workspaceId: parts[2],
        entityType: parts[3],
        id: parts[4],
      };
    }
    case 'room-crdt':
    case 'space-layout':
    case 'space-entities':
    case 'global-entities':
    case 'file-editor':
      return { type, id: parts[2] ?? '' };
    case 'entity-by-id':
    case 'manifest':
      return { type, id: '' };
    default:
      return null;
  }
}

/**
 * Extract the entity ID from any IDB key (new-format or legacy).
 * Returns null if the key format is not recognised.
 */
export function getEntityIdFromKey(key: string): string | null {
  // New-format key
  const parsed = parseIDBKey(key);
  if (parsed) {
    return parsed.id || null;
  }

  // Legacy: river_crdt_{roomId} or river_space_crdt_{roomId}
  // Room IDs look like "doc:entityId" or "note:entityId".
  // Must be checked BEFORE the generic river_{ws}_{type}_{id} pattern,
  // otherwise river_space_crdt_note:abc123 would incorrectly match
  // the generic pattern and return "note:abc123" instead of "abc123".
  const roomMatch = key.match(/^river_(?:space_)?crdt_\w+:(.+)$/);
  if (roomMatch) return roomMatch[1];

  // Legacy: river_file:{entityId}
  const fileMatch = key.match(/^river_file:(.+)$/);
  if (fileMatch) return fileMatch[1];

  // Legacy: river_{workspaceId}_{entityType}_{entityId}
  if (key.startsWith('river_') && !key.startsWith('river__')) {
    const parts = key.split('_');
    if (parts.length >= 4) {
      return parts[parts.length - 1];
    }
  }

  return null;
}

/**
 * Check whether a key belongs to the new managed format.
 */
export function isManagedKey(key: string): boolean {
  return key.startsWith(PREFIX + SEPARATOR);
}

/**
 * Check whether a key is a legacy River IDB key (old naming conventions).
 */
export function isLegacyRiverKey(key: string): boolean {
  if (isManagedKey(key)) return false;
  // All legacy keys start with 'river_' (single underscore).
  // This covers river_{ws}_{type}_{id}, river_crdt_{room},
  // river_space_crdt_{room}, river_file:{id}, river_entity_cache, etc.
  return key.startsWith('river_');
}
